import { DropdownLayout, ListItemSection } from '@wix/design-system';
import { EditSmall, ImageSparklesSmall } from '@wix/wix-ui-icons-common';
import React, { useContext } from 'react';

import { EditorContext, ModalServiceContext, RicosContext } from './../../ricos-context';
import type { ModalService } from './../../ricos-modals';
import type { TiptapEditor } from './../../ricos-types';

import { AI_MODAL_FLOATING_MENU_POPOVER } from '../data-hooks';
import { defaultPromptsConfiguration } from '../prompts';
import { unreachable } from '../ts-utils';
import type { AiPluginEditorConfig, Prompt, PromptsConfiguration } from '../types';
import styles from './ai-actions-modal-scss';

type AiActionsModalProps = {
  config: AiPluginEditorConfig;
  toggleAiContentModal: (
    editor: TiptapEditor,
    modalsService: ModalService,
    initialPrompt?: Prompt
  ) => void;
  toggleAiImageModal: (
    editor: TiptapEditor,
    modalsService: ModalService,
    convertSelectionToPrompt?: boolean
  ) => void;
  enableImageGeneration?: boolean;
};

export const AiActionsModal: React.FC<AiActionsModalProps> = ({
  toggleAiContentModal,
  toggleAiImageModal,
  enableImageGeneration,
  config,
}) => {
  const { t } = useContext(RicosContext);
  const { adapter } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext);
  const [prompts] = React.useState<PromptsConfiguration>(
    config.configurePrompts
      ? config.configurePrompts(defaultPromptsConfiguration)
      : defaultPromptsConfiguration
  );

  const openAiContentModal = React.useCallback(
    (initialPrompt?: Prompt) => {
      toggleAiContentModal(adapter.tiptapEditor, modalService, initialPrompt);
    },
    [adapter.tiptapEditor, modalService]
  );

  const openAiImageModal = React.useCallback(() => {
    toggleAiImageModal(adapter.tiptapEditor, modalService, true);
  }, [adapter.tiptapEditor, modalService]);

  const options = React.useMemo(() => {
    return [
      promptToDropdownOption(
        {
          promptSlug: '',
          type: 'prompt' as const,
          title: 'AIPlugin_Action_WritePrompt',
          icon: EditSmall,
        },
        t,
        () => openAiContentModal()
      ),
      ...(enableImageGeneration
        ? [
            {
              disabled: true,
              overrideOptionStyle: true,
              id: 'AIPlugin_Prompts_Section_Image',
              value: () => <ListItemSection title={t('AIPlugin_Prompts_Section_Image')} />,
            },
            promptToDropdownOption(
              {
                promptSlug: '',
                type: 'prompt' as const,
                title: 'AIPlugin_Action_GenerateImage',
                icon: ImageSparklesSmall,
              },
              t,
              () => openAiImageModal()
            ),
          ]
        : []),
      ...prompts.floatingToolbarButtonPrompts.map(prompt => {
        switch (prompt.type) {
          case 'prompt': {
            return promptToDropdownOption(prompt, t, () => openAiContentModal(prompt));
          }
          case 'section': {
            return {
              disabled: true,
              overrideOptionStyle: true,
              id: prompt.title,
              value: () => <ListItemSection title={t(prompt.title)} />,
            };
          }
          default: {
            throw unreachable(prompt);
          }
        }
      }),
    ];
  }, [prompts.floatingToolbarButtonPrompts]);

  return (
    <DropdownLayout
      dataHook={AI_MODAL_FLOATING_MENU_POPOVER}
      className={styles.dropdown}
      maxHeightPixels={250}
      minWidthPixels={242}
      visible
      options={options}
    />
  );
};

const promptToDropdownOption = (
  prompt: Prompt,
  t: (key: string) => string,
  onClick: () => void
) => {
  return {
    as: 'button',
    dataHook: prompt.title,
    ellipsis: false,
    id: prompt.title,
    onClick,
    optionTitle: t(prompt.title),
    prefixIcon: prompt.icon ? <prompt.icon /> : undefined,
    shouldFocusWithoutScroll: true,
    size: 'small',
    skin: 'standard',
    title: false,
    value: 'action',
  };
};
